// Which items the Items page lists. One checkbox per top-level item; unchecked
// means hidden (ItemVisibility.mjs). Hiding is presentation only -- the item's
// page, the Trips page and the digests keep it -- and this dialog is the ONE
// editor of the flag: it is deliberately not a row in itemFields, which would
// put it in every item edit modal.
//
// A bespoke action dialog, like WeatherEmailModal and ShareTripModal, not an
// autosaved EditForm: a checkbox is a decision, and there is nothing here to
// write per keystroke. Errors show inline because the page's status bar is
// behind the modal. The list is re-read on every open, for ShareTripModal's
// reason: another tab may have renamed or added something.
//
// THE READ WAITS OUT THE item: LANES FIRST. The commonest way here is straight
// out of an item's edit modal, whose Done flushes a rename that may still be
// in flight -- a read issued now would list the OLD name with nothing to
// correct it later (the TripGroup handover rule). The lanes are found by
// prefix off saveQueue.pendingKeys(), never built from ids: nothing here knows
// which item was just edited.
//
// EACH TOGGLE SAVES AT ONCE, through the item's own lane, so the global pill
// shows Saving/Saved/Retry and the tab-close warning covers it. The payload is
// built when the job RUNS, from `desired` -- the newest choice for that item
// -- which is what makes two quick clicks interchangeable under the lane's
// latest-wins coalescing (useAutosave's property). The lane is shared with
// ModalEditForm's autosave, so a job here can still be superseded by an edit
// made to the same item; `onDropped` unwinds it. A drop caused by one of THIS
// dialog's own newer jobs is not a loss (that job writes the same `desired`),
// so jobs are tracked per lane and only the last one standing reverts.

import { useCallback, useEffect, useRef, useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';

import { extractErrorText } from '../../fleet-shared/Common.mjs';
import { isHiddenItem, visibilityValue } from '../../fleet-shared/ItemVisibility.mjs';
import { listAllItems, updateItem } from '../../utilities/Database.mjs';
import { saveQueue } from '../../utilities/SaveQueue.mjs';

const stateLoading = 'loading';
const stateReady = 'ready';
const stateFailed = 'failed';

const ITEM_LANE_PREFIX = 'item:';

export function itemLaneKey(itemId) {
  return `${ITEM_LANE_PREFIX}${itemId}`;
}

// Top-level items only, by name. Sub-equipment is listed on its parent's page
// and never on /me/item, so a checkbox for it would hide nothing.
export function settingsRows(items) {
  return (items ?? [])
    .filter((item) => item && !item.parentId)
    .sort((a, b) => String(a.name ?? '').localeCompare(String(b.name ?? '')));
}

const ItemsSettingsModal = ({ show, onHide, onVisibilityChange }) => {
  const [loadState, setLoadState] = useState(stateLoading);
  const [items, setItems] = useState([]);
  const [error, setError] = useState(null);
  // Bumped by Retry; the load effect keys on it.
  const [generation, setGeneration] = useState(0);

  // id -> hidden, as the user last chose it (what a job writes when it runs).
  const desiredRef = useRef(new Map());
  // id -> hidden, as the server last confirmed it (what a failure reverts to).
  const savedRef = useRef(new Map());
  // id -> Set of this dialog's jobs still queued or in flight on that lane.
  const ownJobsRef = useRef(new Map());

  useEffect(() => {
    if (!show) return undefined;
    let live = true;
    const leaving = new AbortController();
    setLoadState(stateLoading);
    setError(null);
    setItems([]);

    (async () => {
      try {
        const lanes = saveQueue.pendingKeys().filter((key) => key.startsWith(ITEM_LANE_PREFIX));
        await saveQueue.settledAll(lanes, { signal: leaving.signal });
        if (!live) return;
        const rows = settingsRows(await listAllItems());
        if (!live) return;
        const desired = new Map();
        const saved = new Map();
        for (const row of rows) {
          desired.set(row.id, isHiddenItem(row));
          saved.set(row.id, isHiddenItem(row));
        }
        desiredRef.current = desired;
        savedRef.current = saved;
        setItems(rows);
        setLoadState(stateReady);
      } catch (err) {
        if (!live) return;
        setError(extractErrorText(err));
        setLoadState(stateFailed);
      }
    })();

    return () => {
      live = false;
      leaving.abort();
    };
  }, [show, generation]);

  const setRowHidden = useCallback((itemId, hidden) => {
    setItems((prev) =>
      prev.map((row) => (row.id === itemId ? { ...row, visibility: visibilityValue(hidden) } : row))
    );
  }, []);

  // Forget one of this dialog's jobs; true if others of ours remain on the lane.
  const forgetJob = useCallback((itemId, job) => {
    const jobs = ownJobsRef.current.get(itemId);
    if (!jobs) return false;
    jobs.delete(job);
    if (jobs.size === 0) ownJobsRef.current.delete(itemId);
    return jobs.size > 0;
  }, []);

  // Put the row, the page and the next payload back to what the server holds.
  const revert = useCallback(
    (item, message) => {
      const hidden = savedRef.current.get(item.id) ?? false;
      desiredRef.current.set(item.id, hidden);
      setRowHidden(item.id, hidden);
      if (onVisibilityChange) onVisibilityChange(item, hidden);
      setError(message);
    },
    [onVisibilityChange, setRowHidden]
  );

  const handleToggle = useCallback(
    (item, shown) => {
      const hidden = !shown;
      desiredRef.current.set(item.id, hidden);
      setError(null);
      setRowHidden(item.id, hidden);
      if (onVisibilityChange) onVisibilityChange(item, hidden);

      const job = {
        run: () =>
          updateItem({
            id: item.id,
            visibility: visibilityValue(desiredRef.current.get(item.id)),
          }),
        onSaved: (saved) => {
          forgetJob(item.id, job);
          savedRef.current.set(item.id, isHiddenItem(saved));
        },
        onError: (err) => {
          forgetJob(item.id, job);
          revert(item, extractErrorText(err));
        },
        onDropped: () => {
          // Superseded by a newer job on the lane. One of ours writes the
          // same choice, so only a drop that leaves none of ours is a loss.
          if (forgetJob(item.id, job)) return;
          revert(item, `${item.name} was changed by another edit before this could save. Try again.`);
        },
      };
      const jobs = ownJobsRef.current.get(item.id) ?? new Set();
      jobs.add(job);
      ownJobsRef.current.set(item.id, jobs);
      // A checkbox is a discrete control: no debounce.
      saveQueue.schedule(itemLaneKey(item.id), job, { delayMs: 0 });
    },
    [forgetJob, onVisibilityChange, revert, setRowHidden]
  );

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title as="h5">Item settings</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-body-secondary small">
          Checked items are listed on the Items page. Unchecked items are hidden
          from it but keep everything: their pages, trips and logs stay
          reachable from Trips and by link.
        </p>

        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loadState === stateLoading && <p className="mb-0">Loading...</p>}

        {loadState === stateFailed && (
          <Button variant="outline-secondary" onClick={() => setGeneration((g) => g + 1)}>
            Retry
          </Button>
        )}

        {loadState === stateReady && items.length === 0 && (
          <p className="mb-0 text-body-secondary">No items yet.</p>
        )}

        {loadState === stateReady && items.length > 0 && (
          <Form>
            {items.map((item) => (
              // `label` is what gives the box its accessible name -- nothing
              // else may go inside that prop (LogbookViewControls' rule).
              <Form.Check
                key={item.id}
                type="checkbox"
                id={`item-visible-${item.id}`}
                label={item.name}
                checked={!isHiddenItem(item)}
                onChange={(event) => handleToggle(item, event.target.checked)}
              />
            ))}
          </Form>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="primary" onClick={onHide}>
          Done
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ItemsSettingsModal;
